import { useState } from "react";
import axios from "axios";
import { LayoutDashboard, Users, Phone, UserPlus, Plug, Eye, EyeOff } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import CrmDashboard from "./CrmDashboard";
import CrmLeads from "./CrmLeads";
import CrmLeadProfile from "./CrmLeadProfile";
import CrmAddLead from "./CrmAddLead";
import CrmEditLead from "./CrmEditLead";
import CrmCallingPanel from "./CrmCallingPanel";
import CrmCreateUser from "./CrmCreateUser";

const CRM_BASE = "https://crm.smartscholars360.com";

function CrmConnectForm({ onConnected }) {
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleConnect = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${CRM_BASE}/users/login`, null, {
        params: { username: mobile, password },
      });
      const { access_token, user, role, user_id } = res.data;
      localStorage.setItem("crm_token", access_token);
      localStorage.setItem("crm_user", user);
      localStorage.setItem("crm_role", role);
      localStorage.setItem("crm_user_id", user_id);
      onConnected();
    } catch {
      setError("Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center" style={{ minHeight: "400px" }}>
      <Card className="w-[380px]">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <div className="mx-auto mb-3 h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center">
              <Plug className="h-6 w-6 text-blue-600" />
            </div>
            <h5 className="text-lg font-bold text-gray-900 mb-1">Connect to CRM</h5>
            <p className="text-sm text-gray-500">Enter your CRM credentials to access the lead management system.</p>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>
          )}

          <form onSubmit={handleConnect} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Mobile (Username)</Label>
              <Input
                type="text"
                placeholder="Enter mobile number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <><span className="mr-2 h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin inline-block" />Connecting…</>
              ) : (
                <><Plug className="h-4 w-4 mr-1" /> Connect</>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

const CRM_TABS = [
  { key: "crm-dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { key: "crm-leads", icon: Users, label: "Leads" },
  { key: "crm-calling", icon: Phone, label: "Calling Panel" },
  { key: "crm-create-user", icon: UserPlus, label: "Create User", adminOnly: true },
];

export default function CrmContainer() {
  const [connected, setConnected] = useState(!!localStorage.getItem("crm_token"));
  const [view, setView] = useState("crm-dashboard");
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  const crmRole = localStorage.getItem("crm_role");

  const handleConnected = () => setConnected(true);

  const navigate = (target, leadId = null) => {
    setSelectedLeadId(leadId);
    setView(target);
  };

  const disconnectCrm = () => {
    ["crm_token", "crm_user", "crm_role", "crm_user_id"].forEach((k) => localStorage.removeItem(k));
    setConnected(false);
    setView("crm-dashboard");
  };

  if (!connected) {
    return <CrmConnectForm onConnected={handleConnected} />;
  }

  const renderView = () => {
    switch (view) {
      case "crm-dashboard": return <CrmDashboard onNavigate={navigate} />;
      case "crm-leads": return <CrmLeads onNavigate={navigate} />;
      case "crm-lead-profile": return <CrmLeadProfile leadId={selectedLeadId} onBack={() => navigate("crm-leads")} onNavigate={navigate} />;
      case "crm-add-lead": return <CrmAddLead onBack={() => navigate("crm-leads")} />;
      case "crm-edit-lead": return <CrmEditLead leadId={selectedLeadId} onBack={() => navigate("crm-leads")} />;
      case "crm-calling": return <CrmCallingPanel onNavigate={navigate} />;
      case "crm-create-user": return <CrmCreateUser />;
      default: return <CrmDashboard onNavigate={navigate} />;
    }
  };

  const activeTabKey =
    view === "crm-lead-profile" || view === "crm-add-lead" || view === "crm-edit-lead"
      ? "crm-leads"
      : view;

  return (
    <div className="space-y-4">
      {/* CRM Sub-Navigation */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <nav className="flex gap-1 flex-wrap">
          {CRM_TABS.filter((t) => !t.adminOnly || crmRole === "admin").map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTabKey === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => navigate(tab.key)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-blue-50 text-blue-600"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            {localStorage.getItem("crm_user")}
            <span className="ml-1.5 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{crmRole}</span>
          </span>
          <Button variant="outline" size="sm" onClick={disconnectCrm} className="text-red-600 border-red-200 hover:bg-red-50">
            <Plug className="h-3.5 w-3.5 mr-1" /> Disconnect
          </Button>
        </div>
      </div>

      {/* CRM Content */}
      {renderView()}
    </div>
  );
}
